import React from "react";
import { Drawer, Descriptions, Tag, Typography, Card, Space, Button, Divider, message } from "antd";
import { formatDateTime } from "../../../utils/dateUtils";
import { CopyOutlined, EditOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;

const ActionItemDetailDrawer = ({ visible, onClose, actionItem }) => {
    if (!actionItem) return null;

    // 复制JSON内容到剪贴板
    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success(`${label} 已复制到剪贴板`);
        }).catch(() => {
            message.error("复制失败");
        });
    };

    // 渲染JSON字段
    const renderJsonField = (data, title) => {
        if (!data) return null;
        
        const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
        
        return (
            <Card 
                size="small" 
                title={
                    <Space>
                        <span>{title}</span>
                        <Button 
                            type="text" 
                            size="small" 
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(jsonString, title)}
                        >
                            复制
                        </Button>
                    </Space>
                }
                style={{ marginBottom: 16 }}
            >
                <pre style={{ 
                    background: '#f5f5f5', 
                    padding: '12px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    maxHeight: '300px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                }}>
                    {jsonString}
                </pre>
            </Card>
        );
    };

    // 状态标签渲染
    const renderStatusTag = (status) => {
        const statusMap = {
            PENDING_AI: { text: "待AI处理", color: "orange" },
            PENDING_REVIEW: { text: "待人工审核", color: "gold" },
            PROCESSING: { text: "处理中", color: "blue" },
            COMPLETED: { text: "已完成", color: "green" },
            FAILED: { text: "处理失败", color: "red" },
            REJECTED: { text: "已拒绝", color: "gray" },
        };
        const config = statusMap[status] || { text: status, color: "default" };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // 优先级标签渲染
    const renderPriorityTag = (priority) => {
        if (priority === null || priority === undefined) return "-";
        const priorityMap = {
            1: { text: "高", color: "red" },
            2: { text: "中", color: "orange" },
            3: { text: "低", color: "green" },
        };
        const config = priorityMap[priority] || { text: priority, color: "default" };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // 解决状态标签渲染
    const renderResolutionTag = (resolution) => {
        if (!resolution) return "-";
        const resolutionMap = {
            AUTO_EXECUTED: { text: "自动执行", color: "green" },
            USER_CONFIRMED: { text: "用户确认", color: "blue" },
            USER_MODIFIED: { text: "用户修改", color: "orange" },
            USER_REJECTED: { text: "用户拒绝", color: "red" },
        };
        const config = resolutionMap[resolution] || { text: resolution, color: "default" };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    return (
        <Drawer
            title={`行动项详情 - ID: ${actionItem.id}`}
            width={800}
            onClose={onClose}
            open={visible}
            extra={
                <Space>
                    <Button icon={<EditOutlined />} type="primary">
                        编辑
                    </Button>
                </Space>
            }
        >
            <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="ID" span={1}>
                    <Text code>{actionItem.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="状态" span={1}>
                    {renderStatusTag(actionItem.status)}
                </Descriptions.Item>
                
                <Descriptions.Item label="优先级" span={1}>
                    {renderPriorityTag(actionItem.priority)}
                </Descriptions.Item>
                <Descriptions.Item label="解决状态" span={1}>
                    {renderResolutionTag(actionItem.resolution)}
                </Descriptions.Item>

                <Descriptions.Item label="来源类型" span={1}>
                    <Tag color="blue">{actionItem.source_type}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="来源引用ID" span={1}>
                    <Text code>{actionItem.source_ref_id || "-"}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Agent类型" span={1}>
                    {actionItem.agent_type ? <Tag color="purple">{actionItem.agent_type}</Tag> : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="建议变更类型" span={1}>
                    {actionItem.suggested_change_type ? <Tag color="cyan">{actionItem.suggested_change_type}</Tag> : "-"}
                </Descriptions.Item>

                <Descriptions.Item label="关联航班ID" span={1}>
                    {actionItem.flight_operation_id ? <Text code>{actionItem.flight_operation_id}</Text> : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="关联邮件ID" span={1}>
                    {actionItem.related_email_id ? <Text code>{actionItem.related_email_id}</Text> : "-"}
                </Descriptions.Item>

                <Descriptions.Item label="事务类型" span={1}>
                    {actionItem.transaction_type ? <Tag color="geekblue">{actionItem.transaction_type}</Tag> : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="事务ID" span={1}>
                    {actionItem.transaction_id ? <Text code>{actionItem.transaction_id}</Text> : "-"}
                </Descriptions.Item>

                <Descriptions.Item label="关联对象类型" span={1}>
                    {actionItem.related_object_type ? <Tag color="magenta">{actionItem.related_object_type}</Tag> : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="关联对象ID" span={1}>
                    {actionItem.related_object_id ? <Text code>{actionItem.related_object_id}</Text> : "-"}
                </Descriptions.Item>

                <Descriptions.Item label="解决者用户ID" span={1}>
                    {actionItem.resolver_user_id ? <Text code>{actionItem.resolver_user_id}</Text> : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="解决时间" span={1}>
                    {actionItem.resolved_at ? formatDateTime(actionItem.resolved_at) : "-"}
                </Descriptions.Item>

                <Descriptions.Item label="截止日期" span={1}>
                    {actionItem.due_date ? formatDateTime(actionItem.due_date) : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="处理截止时间" span={1}>
                    {actionItem.deadline ? formatDateTime(actionItem.deadline) : "-"}
                </Descriptions.Item>

                <Descriptions.Item label="创建时间" span={1}>
                    {formatDateTime(actionItem.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label="更新时间" span={1}>
                    {formatDateTime(actionItem.updated_at)}
                </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* 文本内容字段 */}
            {actionItem.raw_input_content && (
                <Card size="small" title="原始输入内容" style={{ marginBottom: 16 }}>
                    <Paragraph>
                        <pre style={{ 
                            background: '#f5f5f5', 
                            padding: '12px', 
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all'
                        }}>
                            {actionItem.raw_input_content}
                        </pre>
                    </Paragraph>
                </Card>
            )}

            {actionItem.ai_processing_notes && (
                <Card size="small" title="AI处理备注" style={{ marginBottom: 16 }}>
                    <Paragraph>{actionItem.ai_processing_notes}</Paragraph>
                </Card>
            )}

            {actionItem.user_remarks && (
                <Card size="small" title="用户备注" style={{ marginBottom: 16 }}>
                    <Paragraph>{actionItem.user_remarks}</Paragraph>
                </Card>
            )}

            {/* JSON字段 */}
            {renderJsonField(actionItem.suggested_payload, "建议载荷 (suggested_payload)")}
            {renderJsonField(actionItem.user_modified_payload, "用户修改载荷 (user_modified_payload)")}
            {renderJsonField(actionItem.additional_info, "附加信息 (additional_info)")}
        </Drawer>
    );
};

export default ActionItemDetailDrawer;
